import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AmountVisibilityProvider } from './contexts/AmountVisibilityContext';
import { RoleProtectedRoute, roleHome } from './components/RoleProtectedRoute';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import NewWorkspacePage from './pages/NewWorkspacePage';
import BillingPage from './pages/BillingPage';
import BankAccountsPage from './pages/BankAccountsPage';
import DashboardPage from './pages/DashboardPage';
import { EmployeesPage } from './pages/EmployeesPage';
import { DepartmentsPage } from './pages/DepartmentsPage';
import { DesignationsPage } from './pages/DesignationsPage';
import { AttendancePage } from './pages/AttendancePage';
import { ImportAttendancePage } from './pages/ImportAttendancePage';
import AttendanceReportPage from './pages/AttendanceReportPage';
import { PayrollPage } from './pages/PayrollPage';
import { SalaryHikesPage } from './pages/SalaryHikesPage';
import { ExpensesPage } from './pages/ExpensesPage';
import { ReportsPage } from './pages/ReportsPage';
import { ClientsPage } from './pages/ClientsPage';
import { ProductsPage } from './pages/ProductsPage';
import { TaxesPage } from './pages/TaxesPage';
import { InvoicesPage } from './pages/InvoicesPage';
import CSRMPage from './pages/CSRMPage';
import MarketingPage from './pages/MarketingPage';
import { InvoiceReportsPage } from './pages/InvoiceReportsPage';
import TDSReportPage from './pages/TDSReportPage';
import { CompaniesPage } from './pages/CompaniesPage';
import { QuotesPage } from './pages/QuotesPage';
import { RecurringPage } from './pages/RecurringPage';
import ProposalsPage from './pages/ProposalsPage';
import ProposalsKanbanPage from './pages/ProposalsKanbanPage';
import ProposalsStaffOverviewPage from './pages/ProposalsStaffOverviewPage';
import UsersPage from './pages/UsersPage';
import LeadsPage from './pages/LeadsPage';
import LeavesPage from './pages/LeavesPage';
import IntegrationsPage from './pages/IntegrationsPage';
import CheckInPage from './pages/CheckInPage';
import CheckInDashboardPage from './pages/CheckInDashboardPage';
import AssetsPage from './pages/AssetsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import ProjectsPage from './pages/ProjectsPage';
import TaskBoardPage from './pages/TaskBoardPage';
import StandupReportPage from './pages/StandupReportPage';
import ClientWorkboardPage from './pages/ClientWorkboardPage';
import HostingsPage from './pages/HostingsPage';
import MyDashboardPage from './pages/MyDashboardPage';
import MyTasksPage from './pages/MyTasksPage';
import MyAttendancePage from './pages/MyAttendancePage';
import MyLeavesPage from './pages/MyLeavesPage';
import RemindersPage from './pages/RemindersPage';
import VaultPage from './pages/VaultPage';
import PersonalVaultPage from './pages/PersonalVaultPage';
import GstFilingPage from './pages/GstFilingPage';
import ProfitLossPage from './pages/ProfitLossPage';
import MyPermissionsPage from './pages/MyPermissionsPage';
import PermissionRequestPublicPage from './pages/PermissionRequestPublicPage';
import PermissionBoardPage from './pages/PermissionBoardPage';
import ProductivityPage from './pages/ProductivityPage';
import TimerProductivityPage from './pages/TimerProductivityPage';
import CoordinatorDashboardPage from './pages/CoordinatorDashboardPage';
import ChatPage from './pages/ChatPage';
import { Toaster } from './components/ui/sonner';
import '@/App.css';

// Smart redirect for "/" — sends each role to their home
const HomeRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? roleHome(user.role, user.is_team_lead) : '/login'} replace />;
};

// Switch the /proposals page based on role: admin sees Kanban, staff sees stats overview
const ProposalsRouteSwitch = () => {
  const { user } = useAuth();
  return user?.role === 'admin' ? <ProposalsKanbanPage /> : <ProposalsStaffOverviewPage />;
};

function App() {
  return (
    <AuthProvider>
      <AmountVisibilityProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route
            path="/workspaces/new"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <NewWorkspacePage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <BillingPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/bank-accounts"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <BankAccountsPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route path="/checkin" element={<CheckInPage />} />
          <Route path="/permission-request" element={<PermissionRequestPublicPage />} />
          <Route path="/" element={<HomeRedirect />} />
          <Route
            path="/dashboard"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <EmployeesPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/departments"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <DepartmentsPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/designations"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <DesignationsPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/attendance"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <AttendancePage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/import-attendance"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <ImportAttendancePage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/attendance-report"
            element={
              <RoleProtectedRoute adminOnly>
                <Layout>
                  <AttendanceReportPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/payroll"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <PayrollPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/salary-hikes"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <SalaryHikesPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/expenses"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <ExpensesPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <ReportsPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <ClientsPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <ProductsPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/taxes"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <TaxesPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <InvoicesPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/csrm"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <CSRMPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/marketing"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <MarketingPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/invoice-reports"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <InvoiceReportsPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/tds-report"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <TDSReportPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/companies"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <CompaniesPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/quotes"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <QuotesPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/recurring"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <RecurringPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/proposals"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <ProposalsRouteSwitch />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/proposals/builder"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <ProposalsPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <RoleProtectedRoute adminOnly>
                <Layout>
                  <UsersPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/leads"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <LeadsPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/leaves"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <LeavesPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <RoleProtectedRoute adminOnly>
                <Layout>
                  <IntegrationsPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/checkin-dashboard"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <CheckInDashboardPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/assets"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <AssetsPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/appointments"
            element={
              <RoleProtectedRoute>
                <Layout>
                  <AppointmentsPage />
                </Layout>
              </RoleProtectedRoute>
            }
          />
          {/* ---------- Project & Task Management (admin + team_lead) ---------- */}
          <Route
            path="/projects"
            element={
              <RoleProtectedRoute>
                <Layout><ProjectsPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/tasks/board"
            element={
              <RoleProtectedRoute>
                <Layout><TaskBoardPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/standup"
            element={
              <RoleProtectedRoute>
                <Layout><StandupReportPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/client-workboard"
            element={
              <RoleProtectedRoute>
                <Layout><ClientWorkboardPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/hostings"
            element={
              <RoleProtectedRoute>
                <Layout><HostingsPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          {/* ---------- Staff Workspace (every authenticated user) ---------- */}
          <Route
            path="/my/dashboard"
            element={
              <RoleProtectedRoute>
                <Layout><MyDashboardPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/my/tasks"
            element={
              <RoleProtectedRoute>
                <Layout><MyTasksPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/my/attendance"
            element={
              <RoleProtectedRoute>
                <Layout><MyAttendancePage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/my/leaves"
            element={
              <RoleProtectedRoute>
                <Layout><MyLeavesPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/my/permissions"
            element={
              <RoleProtectedRoute>
                <Layout><MyPermissionsPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/reminders"
            element={
              <RoleProtectedRoute>
                <Layout><RemindersPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/vault"
            element={
              <RoleProtectedRoute>
                <Layout><VaultPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/personal-vault"
            element={
              <RoleProtectedRoute>
                <Layout><PersonalVaultPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/gst-filing"
            element={
              <RoleProtectedRoute>
                <Layout><GstFilingPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/profit-loss"
            element={
              <RoleProtectedRoute>
                <Layout><ProfitLossPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/permissions"
            element={
              <RoleProtectedRoute>
                <Layout><PermissionBoardPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/productivity"
            element={
              <RoleProtectedRoute>
                <Layout><ProductivityPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/time-tracking"
            element={
              <RoleProtectedRoute>
                <Layout><TimerProductivityPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/coordinator"
            element={
              <RoleProtectedRoute>
                <Layout><CoordinatorDashboardPage /></Layout>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <RoleProtectedRoute>
                <Layout><ChatPage /></Layout>
              </RoleProtectedRoute>
            }
          />
        </Routes>
        <Toaster />
      </BrowserRouter>
      </AmountVisibilityProvider>
    </AuthProvider>
  );
}

export default App;
